import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box } from '@mui/material';
import { colors, spacing, borderRadius, shadows } from '../../constants';
import { useEditQueryModalState } from '../../hooks/queries/useEditQueryModalState';
import { useEditQueryModalData } from '../../hooks/queries/useEditQueryModalData';
import { useEditQueryModalForm } from '../../hooks/queries/useEditQueryModalForm';
import { queryService } from '../../services/queryService';
import EditQueryModalHeader from './editQueryModal/EditQueryModalHeader';
import EditQueryModalFooter from './editQueryModal/EditQueryModalFooter';
import EditQueryBasicInfoSection from './editQueryModal/EditQueryBasicInfoSection';
import EditQueryDetailsSection from './editQueryModal/EditQueryDetailsSection';
import EditQueryAssignmentSection from './editQueryModal/EditQueryAssignmentSection';
import { useProjectsCatalog } from '../../hooks/useProjectsCatalog';

/**
 * EditQueryModal Component
 * Modal for editing query details
 * 
 * @param {boolean} open - Whether modal is open
 * @param {Function} onClose - Close handler
 * @param {Object} query - Query object to edit
 * @param {Function} onSuccess - Success callback
 * @param {Function} onQueryUpdated - Alias for onSuccess (used by some pages)
 */
const EditQueryModal = ({ open, onClose, query, onSuccess, onQueryUpdated }) => {
  const successCallback = onSuccess || onQueryUpdated;
  const [queryDetails, setQueryDetails] = useState(null);
  const projectsCatalog = useProjectsCatalog(open);

  useEffect(() => {
    let ignore = false;

    const fetchQueryDetails = async () => {
      if (!open || !query?._id) {
        setQueryDetails(null);
        return;
      }

      setQueryDetails(query);

      try {
        const fullQuery = await queryService.getQueryById(query._id);
        if (!ignore) {
          setQueryDetails(fullQuery || query);
        }
      } catch (_error) {
        if (!ignore) {
          setQueryDetails(query);
        }
      }
    };

    fetchQueryDetails();

    return () => {
      ignore = true;
    };
  }, [open, query]);

  const activeQuery = queryDetails || query;

  // State management hook
  const {
    loading,
    setLoading,
    formData,
    errors,
    setErrors,
    focusedFields,
    existingAttachments,
    newFiles,
    removedAttachments,
    attachmentInputKey,
    currentUser,
    canManageAssignments,
    handleChange,
    handlePriorityChange,
    handleAssigneeChange,
    handleFocus,
    handleBlur,
    handleNewFilesChange,
    handleRemoveNewFile,
    handleRemoveExistingAttachment,
  } = useEditQueryModalState({ open, query: activeQuery });

  // Data fetching hook
  const { users, loadingUsers } = useEditQueryModalData({
    open,
    query: activeQuery,
    currentUser,
  });

  // Form submission hook
  const { handleSubmit } = useEditQueryModalForm({
    formData,
    query: activeQuery,
    newFiles,
    removedAttachments,
    setErrors,
    setLoading,
    onSuccess: successCallback,
    onClose,
  });

  if (!activeQuery) return null;

  return (
    <>
      <style>
        {`
          .edit-query-modal input:not(:focus):hover,
          .edit-query-modal select:not(:focus):hover,
          .edit-query-modal textarea:not(:focus):hover {
            border-color: ${colors.borderHover} !important;
          }
          .edit-query-modal input:disabled,
          .edit-query-modal select:disabled {
            cursor: not-allowed;
            opacity: 0.6;
          }
        `}
      </style>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className="edit-query-modal"
        PaperProps={{
          sx: {
            borderRadius: borderRadius.modal,
            boxShadow: shadows.xl,
            maxHeight: '95vh',
            overflow: 'hidden',
          },
        }}
      >
        <EditQueryModalHeader onClose={onClose} />

        <DialogContent
          sx={{
            padding: `${spacing.sm} ${spacing.md}`,
            paddingBottom: spacing.sm,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,249,250,0.9) 100%)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '10px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f5f9',
              borderRadius: '5px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: '5px',
              '&:hover': {
                background: '#0e172a',
              },
            },
          }}
        >
          <Box display="flex" flexDirection="column" gap={spacing.sm} sx={{ pt: spacing.sm, pb: 0 }}>
            <EditQueryBasicInfoSection
              query={activeQuery}
              projectsCatalog={projectsCatalog}
              formData={formData}
              handleChange={handleChange}
              handlePriorityChange={handlePriorityChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              focusedFields={focusedFields}
            />

            <EditQueryDetailsSection
              formData={formData}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              focusedFields={focusedFields}
              errors={errors}
              existingAttachments={existingAttachments}
              newFiles={newFiles}
              attachmentInputKey={attachmentInputKey}
              onNewFilesChange={handleNewFilesChange}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onRemoveNewFile={handleRemoveNewFile}
            />

            <EditQueryAssignmentSection
              query={activeQuery}
              formData={formData}
              users={users}
              handleAssigneeChange={handleAssigneeChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              focusedFields={focusedFields}
              errors={errors}
              loadingUsers={loadingUsers}
              canManageAssignments={canManageAssignments}
            />
          </Box>
        </DialogContent>

        <EditQueryModalFooter onClose={onClose} onSubmit={handleSubmit} loading={loading} />
      </Dialog>
    </>
  );
};

export default EditQueryModal;
